<script setup lang="ts">
  import { computed, onBeforeUnmount, onMounted, ref, type PropType } from 'vue'

  import { v4 as uuidv4 } from 'uuid'

  import { useI18n } from 'vue-i18n'

  import { UploadCloud01Icon } from '@gohighlevel/ghl-icons/24/outline'

  import { validateImageFiles } from '@common/media-layer/utils/image.utils'

  import { validateVideoFiles } from '@common/media-layer/utils/video.utils'

  import { validateDocumentFiles } from '@common/media-layer/utils/document-validation.utils'
  import { isVoiceNoteAudioFile } from '@common/media-layer/utils/voice-note'

  import CPText from '@ui/components/CPText.vue'

  import type { ValidationError } from '../types/validation.types'
  import type { DragDropValidation, DroppedFile } from '../types/drag-drop.types'

  const props = defineProps({
    // Whether drag and drop is disabled
    disabled: {
      type: Boolean,
      default: false,
    },
    // Size variant for overlay content
    variant: {
      type: String as PropType<'sm' | 'lg'>,
      default: 'lg',
    },
    // Custom overlay message
    overlayMessage: {
      type: String,
      default: '',
    },
    // Accept specific file types (e.g., "image/*,.pdf")
    accept: {
      type: String,
      default: '',
    },
    // Validation rules (same structure as editor tools)
    validation: {
      type: Object as PropType<DragDropValidation>,
      default: undefined,
    },
  })

  const emit = defineEmits<{
    filesDropped: [files: DroppedFile[]]
    validationError: [errors: ValidationError[]]
  }>()

  const { t } = useI18n()

  // Drag state
  const isDragging = ref(false)

  const dragCounter = ref(0)

  // Variant-based sizing
  const sizeConfig = computed(() => {
    switch (props.variant) {
      case 'sm':
        return {
          iconContainer: 'w-8 h-8 mb-2',
          icon: 'w-6 h-6',
          messageSize: 'md' as const,
          subTextSize: 'xs' as const,
        }
      case 'lg':
      default:
        return {
          iconContainer: 'w-16 h-16 mb-4',
          icon: 'w-8 h-8',
          messageSize: '2xl' as const,
          subTextSize: 'lg' as const,
        }
    }
  })

  // Computed classes
  // Overlay is visual-only — drag/drop listeners live on the parent. Always
  // `pointer-events-none` so a stuck `isDragging` (missed dragleave) cannot
  // swallow clicks on the composer or anything stacked beneath a large zone.
  const overlayClasses = computed(() => {
    const base = 'absolute inset-0 z-50 pointer-events-none transition-opacity duration-200 flex items-center justify-center'
    const visible = isDragging.value ? 'opacity-100' : 'opacity-0'
    const styles = 'bg-primary-50 border-2 border-dashed border-primary rounded-lg'
    return `${base} ${visible} ${styles}`
  })

  const resetDragState = () => {
    dragCounter.value = 0
    isDragging.value = false
  }

  // Drag event handlers
  const handleDragEnter = (event: DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    if (props.disabled) return

    dragCounter.value++
    if (dragCounter.value === 1) {
      isDragging.value = true
    }
  }

  const handleDragOver = (event: DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    if (props.disabled) return

    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy'
    }
  }

  const handleDragLeave = (event: DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    if (props.disabled) return

    dragCounter.value--
    if (dragCounter.value === 0) {
      isDragging.value = false
    }
  }

  const handleDrop = async (event: DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    if (props.disabled) return

    // Reset drag state
    resetDragState()

    // Get dropped files
    const files = event.dataTransfer?.files
    if (!files || files.length === 0) return

    await processFiles(Array.from(files))
  }

  // Detect file type (audio uses voice-note MIME allowlist so clips aren't treated as documents)
  const detectFileType = (file: File): 'image' | 'video' | 'audio' | 'document' => {
    if (file.type.startsWith('image/')) return 'image'
    if (file.type.startsWith('video/')) return 'video'
    if (isVoiceNoteAudioFile(file)) return 'audio'
    return 'document'
  }

  // Process and validate files
  const processFiles = async (files: File[]) => {
    const allErrors: ValidationError[] = []
    const validFiles: File[] = []

    // Separate files by type
    const imageFiles = files.filter(f => detectFileType(f) === 'image')
    const videoFiles = files.filter(f => detectFileType(f) === 'video')
    const audioFiles = files.filter(f => detectFileType(f) === 'audio')
    const documentFiles = files.filter(f => detectFileType(f) === 'document')

    // Validate images (only file size, type, dimensions - maxFiles handled at composable level)
    if (imageFiles.length > 0 && props.validation?.mediaValidations?.imageValidations) {
      const { valid, errors } = await validateImageFiles(imageFiles, props.validation.mediaValidations.imageValidations)
      validFiles.push(...valid)
      allErrors.push(...errors)
    } else if (imageFiles.length > 0) {
      validFiles.push(...imageFiles)
    }

    // Validate videos (only file size, type, duration - maxFiles handled at composable level)
    if (videoFiles.length > 0 && props.validation?.mediaValidations?.videoValidations) {
      const { valid, errors } = await validateVideoFiles(videoFiles, props.validation.mediaValidations.videoValidations)
      validFiles.push(...valid)
      allErrors.push(...errors)
    } else if (videoFiles.length > 0) {
      validFiles.push(...videoFiles)
    }

    // Validate voice-note audio (size/type via audioValidations; same shape as FileUploadValidation)
    if (audioFiles.length > 0 && props.validation?.audioValidations) {
      const { valid, errors } = await validateDocumentFiles(audioFiles, props.validation.audioValidations)
      validFiles.push(...valid)
      allErrors.push(...errors)
    } else if (audioFiles.length > 0) {
      validFiles.push(...audioFiles)
    }

    // Validate documents (only file size, type - maxFiles handled at composable level)
    if (documentFiles.length > 0 && props.validation?.fileValidations) {
      const { valid, errors } = await validateDocumentFiles(documentFiles, props.validation.fileValidations)
      validFiles.push(...valid)
      allErrors.push(...errors)
    } else if (documentFiles.length > 0) {
      validFiles.push(...documentFiles)
    }

    // Emit errors if any
    if (allErrors.length > 0) {
      emit('validationError', allErrors)
    }

    // Emit valid files
    if (validFiles.length > 0) {
      const droppedFiles: DroppedFile[] = validFiles.map(file => ({
        _id: uuidv4(),
        file,
        url: URL.createObjectURL(file),
        name: file.name,
        size: file.size,
        type: file.type,
      }))

      emit('filesDropped', droppedFiles)
    }
  }

  onMounted(() => {
    if (typeof window === 'undefined') return
    window.addEventListener('dragend', resetDragState)
    window.addEventListener('blur', resetDragState)
  })

  onBeforeUnmount(() => {
    if (typeof window === 'undefined') return
    window.removeEventListener('dragend', resetDragState)
    window.removeEventListener('blur', resetDragState)
  })
</script>

<template>
  <div class="relative" @dragenter="handleDragEnter" @dragover="handleDragOver" @dragleave="handleDragLeave" @drop="handleDrop">
    <!-- Overlay -->
    <div id="drag-drop-zone-overlay" :class="overlayClasses">
      <div class="text-center px-6">
        <div class="mx-auto flex items-center justify-center rounded-full bg-primary-100" :class="sizeConfig.iconContainer">
          <UploadCloud01Icon class="text-primary" :class="sizeConfig.icon" />
        </div>
        <CPText :size="sizeConfig.messageSize" weight="semibold" class="text-primary-active mb-1">
          {{ overlayMessage || t('editor.dragDrop.dropMessage') }}
        </CPText>
        <CPText :size="sizeConfig.subTextSize" class="text-primary">{{ t('editor.dragDrop.releaseMessage') }}</CPText>
      </div>
    </div>

    <!-- Children -->
    <slot :is-dragging="isDragging" />
  </div>
</template>
